import { LoadingOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import PermissionCheck from "../../Common/PermissionCheck";
import type { PermissionNames } from "~enums/permissions";
// TODO: remove useDebounce when this component will be removed (useDebounce is used only here)
import useDebounce from "../../hooks/useDebounce";
import { useApi } from "../../shared/api";

interface OptionType {
  label: string;
  value: string;
}

interface Props {
  style?: React.CSSProperties;
  handleSelect: (value: { label: string; value: string } | string | Array<{ label: string; value: string }>) => void;
  URL: string;
  searchParam?: string;
  placeholder?: string;
  parseFunction: (item: any) => { label: string; value: string };
  limit?: number;
  permissionName?: PermissionNames;
  loading?: boolean;
  disablePreload?: boolean;
  defaultValue?: { value: string; label: string };
  multi?: boolean;
  handleClear: () => void;
}

/**
 * Represents a component that allows users to select options from an infinite list.
 *
 * @param {function} handleSelect - The function to be called when an option is selected.
 * @param {string} URL - The URL for fetching data.
 * @param {string} searchParam - The parameter to be used for search. Disable search if no present
 * @param {string} placeholder - The placeholder text to be displayed in the select input.
 * @param {function} parseFunction - The function used to parse data received from the API.
 * @param {number} limit - The maximum number of options to be loaded at a time.
 * @param {boolean} loading - Indicates whether the component is in a loading state.
 * @param {object} style - Any custom styles to be applied to the component.
 * @param defaultValue - default value data
 * @param multi - allow multi select, handleSelect function should correct work with it
 * @param handleClear - function to clear select value
 */
const InfinitySelectComponent = ({
  handleSelect,
  URL,
  searchParam,
  placeholder = "All",
  parseFunction,
  limit = 50,
  loading,
  style,
  defaultValue,
  multi,
  handleClear,
}: Props) => {
  const { fetchData } = useApi();
  const [options, setOptions] = useState<OptionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (defaultValue?.value) {
      setOptions((prev) => [...prev.filter((item) => item.value !== defaultValue?.value), defaultValue]);
    }
  }, [defaultValue]);

  const loadOptions = useCallback(
    (search: string, newPage: number) => {
      let reqUrl = `${URL}?limit=${limit}&offset=${limit * newPage}`;
      if (search && search.length >= 2) {
        reqUrl = `${reqUrl}&${searchParam}=${search}`;
      }
      setIsLoading(true);
      fetchData({ url: reqUrl })
        .then((data) => {
          if (newPage === 0) {
            const options = data?.data
              .map(parseFunction)
              .filter((item: OptionType) => item.value !== defaultValue?.value);
            if (defaultValue?.value) {
              setOptions([defaultValue, ...options]);
            } else {
              setOptions(options);
            }
          } else {
            setOptions((prevOptions) => [...prevOptions, ...(data || { data: [] }).data.map(parseFunction)]);
          }
          setHasNextPage(!!data?.links?.next);
        })
        .finally(() => setIsLoading(false));
    },
    [URL, fetchData, searchParam, parseFunction, limit, defaultValue],
  );

  useEffect(() => {
    if (isOpen) {
      loadOptions(debouncedSearchValue, page);
    }
  }, [debouncedSearchValue, page, isOpen]);

  const handlePopupScroll = (event: { target: any }) => {
    const target = event.target;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      if (hasNextPage && !isLoading) {
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPage(0);
  };

  const getSuffixIcon = useMemo(() => {
    if (isLoading) {
      return <LoadingOutlined spin />;
    }
    return null;
  }, [isLoading]);

  return (
    <Select
      style={{ width: 120, ...style }}
      showSearch={!searchParam}
      labelInValue
      filterOption={false}
      onSearch={handleSearch}
      suffixIcon={getSuffixIcon}
      onPopupScroll={handlePopupScroll}
      options={options}
      placeholder={placeholder}
      onSelect={handleSelect}
      onClear={handleClear}
      defaultValue={defaultValue}
      disabled={loading}
      onDropdownVisibleChange={setIsOpen}
      mode={multi ? "multiple" : undefined}
      allowClear
    />
  );
};

const OldInfinitySelect = (props: Props) => {
  const { permissionName, defaultValue } = props;
  if (!permissionName) {
    return <InfinitySelectComponent {...props} />;
  }
  return (
    <PermissionCheck
      permissionKeys={[permissionName]}
      componentToRender={<Select defaultValue={defaultValue} style={props.style} disabled />}
    >
      <InfinitySelectComponent {...props} />
    </PermissionCheck>
  );
};

// TODO: Replace with ../components/InfinitySelect/InfinitySelect.ts component
export default OldInfinitySelect;
